import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";

const selCls = "bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet";
const btnCls = "flex items-center gap-2 text-sm rounded-lg border border-navy/10 bg-white px-3 py-2 hover:border-violet/40 transition-colors disabled:opacity-50";
const colName = (headers, j) => headers[j]?.trim() || `Col ${String.fromCharCode(65 + (j % 26))}`;

function RowModal({ libro, solapa, headers, row, onClose, onSaved }) {
  const [vals, setVals] = useState(() => {
    const base = row ? [...row.data] : [];
    while (base.length < headers.length) base.push("");
    return base;
  });
  const [sugs, setSugs] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/correctivos/sugerencias", { params: { libro, solapa } }).then((r) => {
      setSugs(r.data.sugerencias || {});
      if (!row) {
        const pf = r.data.prefill || {};
        setVals((v) => v.map((x, j) => (x === "" && pf[String(j)] ? pf[String(j)] : x)));
      }
    }).catch(() => {});
  }, [libro, solapa, row]);
  const save = async () => {
    setSaving(true);
    try {
      if (row) await api.put(`/correctivos/rows/${row.id}`, { data: vals });
      else await api.post("/correctivos/rows", { libro, solapa, data: vals });
      toast.success(row ? "Fila actualizada" : "Registro agregado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={onClose} data-testid="corr-row-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-navy">{row ? "Editar registro" : "Nuevo registro"}</h2>
            <p className="text-xs text-navy/40">{solapa} · libro {libro}{row?.origen === "sheet" ? " · importado de Google Sheets" : ""}</p>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="corr-row-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-2">
          {vals.map((v, j) => (
            <label key={j} className="text-xs text-navy/50">
              {colName(headers, j)}
              <input value={v} onChange={(e) => setVals(vals.map((x, k) => (k === j ? e.target.value : x)))}
                list={sugs[String(j)] ? `corr-dl-${j}` : undefined}
                className="mt-1 w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-violet"
                data-testid={`corr-field-${j}`} />
              {sugs[String(j)] && (
                <datalist id={`corr-dl-${j}`}>
                  {sugs[String(j)].map((s) => <option key={s} value={s} />)}
                </datalist>
              )}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className={btnCls}>Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="corr-row-save"
            className="flex items-center gap-2 text-sm rounded-lg bg-navy text-white px-5 py-2 hover:bg-navy/90 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Correctivos() {
  const [estado, setEstado] = useState(null);
  const [libro, setLibro] = useState("");
  const [solapa, setSolapa] = useState("");
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState(null); // {row|null}
  const reqId = useRef(0);
  const LIMIT = 100;

  const loadEstado = useCallback(async () => {
    try {
      const r = await api.get("/correctivos/estado");
      setEstado(r.data);
      setSyncing(r.data.syncing);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    loadEstado().then((e) => {
      const l = e?.libros?.find((x) => x.solapas.length) || e?.libros?.[0];
      if (l && !libro) { setLibro(l.libro); setSolapa(l.solapas[0]?.solapa || ""); }
    });
  }, [loadEstado]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(() => setQDeb(q), 400); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(0); }, [libro, solapa, qDeb]);

  const loadRows = useCallback(async () => {
    if (!libro || !solapa) return;
    const id = ++reqId.current;
    setLoading(true);
    try {
      const r = await api.get("/correctivos/rows", { params: { libro, solapa, q: qDeb, offset: page * LIMIT, limit: LIMIT } });
      if (id !== reqId.current) return;
      setData(r.data);
    } catch (e) {
      if (id === reqId.current) toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar la solapa");
    } finally { if (id === reqId.current) setLoading(false); }
  }, [libro, solapa, qDeb, page]);
  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(async () => {
      const e = await loadEstado();
      if (e && !e.syncing) { toast.success("Sincronización terminada"); loadRows(); }
    }, 8000);
    return () => clearInterval(t);
  }, [syncing, loadEstado, loadRows]);

  const syncNow = async () => {
    try {
      await api.post("/correctivos/sync");
      setSyncing(true);
      toast.info("Sincronizando desde Google Sheets…");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const delRow = async (row) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/correctivos/rows/${row.id}`);
      toast.success("Registro eliminado");
      loadRows();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const [exporting, setExporting] = useState(false);
  const exportSolapa = async () => {
    setExporting(true);
    try {
      const r = await api.get("/correctivos/export", { params: { libro, solapa }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `correctivos_${libro}_${solapa}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar"); }
    finally { setExporting(false); }
  };

  const libroSel = estado?.libros?.find((l) => l.libro === libro);
  const headers = data?.headers?.length ? data.headers : Array.from({ length: Math.max(5, ...(data?.rows || []).map((r) => r.data.length)) }, () => "");
  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  return (
    <OSLayout title="OMNIA Correctivos">
      <div className="space-y-5" data-testid="correctivos-page">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-navy/10 overflow-hidden" data-testid="corr-libro-tabs">
            {(estado?.libros || []).map((l) => (
              <button key={l.libro} onClick={() => { setLibro(l.libro); setSolapa(l.solapas[0]?.solapa || ""); }}
                data-testid={`corr-libro-${l.libro}`}
                className={`px-4 py-2 text-sm transition-colors ${libro === l.libro ? "bg-navy text-white" : "bg-white text-navy/60 hover:text-navy"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <select value={solapa} onChange={(e) => setSolapa(e.target.value)} className={`${selCls} max-w-xs`} data-testid="corr-solapa-select">
            {(libroSel?.solapas || []).map((s) => (
              <option key={s.solapa} value={s.solapa}>{s.solapa} ({s.filas_sheet + s.filas_omnia})</option>
            ))}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-navy/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en la solapa…"
              className={`${selCls} pl-9 w-64`} data-testid="corr-search-input" />
          </div>
          <button onClick={() => setModal({ row: null })} disabled={!solapa} data-testid="corr-add-btn"
            className="flex items-center gap-2 text-sm rounded-lg bg-navy text-white px-4 py-2 hover:bg-navy/90 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Nuevo registro
          </button>
          <button onClick={syncNow} disabled={syncing} data-testid="corr-sync-btn" className={btnCls}>
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? "Sincronizando…" : "Sincronizar ahora"}
          </button>
          <button onClick={exportSolapa} disabled={exporting || !solapa} data-testid="corr-export-btn" className={btnCls}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportar Excel
          </button>
          {estado?.last_sync && <span className="text-xs text-navy/35">última sync {new Date(estado.last_sync).toLocaleString("es-AR")} · se actualiza sola cada 15 min</span>}
        </div>
        <p className="text-xs text-navy/35 -mt-2">Las Google Sheets se importan automáticamente. Lo que cargues o edites acá queda en OMNIA y no se pisa con la sincronización.</p>

        {!estado?.libros?.length || !libroSel?.solapas?.length ? (
          <div className="text-sm text-navy/45 py-14 text-center">
            {estado === null ? <Loader2 className="w-5 h-5 animate-spin inline" /> : "Todavía no hay datos: toca \"Sincronizar ahora\" para importar las planillas."}
          </div>
        ) : (
          <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="corr-grid">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-navy text-sm">{solapa} <span className="text-navy/35 font-normal">· libro {libro}</span></h3>
              <div className="flex items-center gap-3 text-xs text-navy/40">
                <span data-testid="corr-total">{data?.total ?? 0} filas{qDeb ? " (filtradas)" : ""}</span>
                {pages > 1 && (
                  <span className="flex items-center gap-2">
                    <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="text-violet disabled:text-navy/20" data-testid="corr-prev">←</button>
                    pág. {page + 1}/{pages}
                    <button onClick={() => setPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1} className="text-violet disabled:text-navy/20" data-testid="corr-next">→</button>
                  </span>
                )}
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </div>
            </div>
            <div className="overflow-auto max-h-[560px]">
              <table className="w-full text-xs whitespace-nowrap">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-navy/40 border-b border-navy/10">
                    <th className="py-2 pr-2 font-medium w-16"></th>
                    {headers.map((h, j) => <th key={j} className="py-2 pr-3 font-medium">{colName(headers, j)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows || []).map((r) => (
                    <tr key={r.id} className="border-b border-navy/5 last:border-0 hover:bg-violet/[0.03] group" data-testid="corr-row">
                      <td className="py-1 pr-2">
                        <span className="flex items-center gap-1.5">
                          <button onClick={() => setModal({ row: r })} title="Editar" data-testid={`corr-edit-${r.id}`}
                            className="text-navy/25 hover:text-violet"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => delRow(r)} title="Eliminar" data-testid={`corr-del-${r.id}`}
                            className="text-navy/25 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          {r.origen === "omnia" && <span className="text-[9px] px-1 rounded bg-violet/10 text-violet font-semibold">OMNIA</span>}
                          {r.edited && <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700 font-semibold">EDIT</span>}
                        </span>
                      </td>
                      {headers.map((_, j) => <td key={j} className="py-1.5 pr-3 text-navy/70">{r.data[j] ?? ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {modal && (
          <RowModal libro={libro} solapa={solapa} headers={headers} row={modal.row}
            onClose={() => setModal(null)} onSaved={() => { setModal(null); loadRows(); }} />
        )}
      </div>
    </OSLayout>
  );
}
